import styled from 'styled-components';
import { BubbleGrey } from '../../components/shared/Bubble';
import { FlexCol, FlexColMiddle, FlexRowMiddle } from '../../components/shared/Flex';
import {
	panelHeaderTop,
	Table,
	TableBody,
	TableHead,
	tablePaddingSide,
	TableRow,
	TD,
	TH,
} from '../../components/shared/Table';
import { Text } from '../../components/shared/Text';
import { EditCell } from '../../components/shared/EditCell';
import {
	checkCurrencyFormatFull,
	checkCurrencyFormatPartly,
	checkDateSchemaFormatFull,
	checkDateSchemaFormatPartly,
	downloadCSVFile,
	formatCurrency,
} from '../../scripts/scripts';
import { EditCellCurrency } from '../../components/shared/EditCellCurrency';
import {
	TixCompletedStatusTypes,
	TixCurrencyTypes,
	TixEvent,
	TixOrder,
	TixSale,
	TixSecondaryTypes,
	TixTransferTypes,
} from '../../types/TixTypes';
import { Icon } from '../../components/shared/Icon';
import IconSeat from '../../../public/icons/seat.svg?react';
import { GETSalesResponse, GETSalesResponseEventBASE, StatsBase } from '../../types/TixAPITypes';
import { useLoadingStore } from '../../store/useLoadingStore';
import { ButtonGrey, ButtonRed, ButtonWhite, ButtonYellow } from '../../components/shared/Buttons';
import { useMemo, useState } from 'react';
import { EditSaleAPIFunc } from '../../scripts/hooks/useEditSaleAPI';
import BigNumber from 'bignumber.js';
import IconWarning from '../../../public/icons/warning.svg?react';
import { OnClickEditSaleModal } from '../../components/modals/ModalEditSale';
import { OnClickEditOrderModal } from '../../components/modals/ModalEditOrder';
import _ from 'lodash';
import { requestData } from '../../scripts/api/api_v2';
import toast from 'react-hot-toast';
import { OSStatsHeader } from '../../components/orders-sales/OSStatsHeader';

const seatColomns = '70px 50px 50px';
const orderColumns = `100px minmax(125px, 1fr) ${seatColomns} 125px 100px 125px minmax(175px, 1fr) minmax(100px, 1fr) 165px 165px 165px minmax(175px, 1fr) 150px 120px 120px`;

const StyledPanelHeader = styled(FlexRowMiddle)`
	padding: ${panelHeaderTop} ${tablePaddingSide};
	display: grid;
	grid-template-columns: 4fr auto 120px minmax(0, 600px);
	column-gap: 6rem;
	background-color: ${({ theme }) => theme.panel_main_bg};
`;

const StyledQuaterHeader = styled(StyledPanelHeader)`
	background-color: ${({ theme }) => theme.panel_highlight_bg};
	border-bottom: 2px solid ${({ theme }) => theme.panel_highlight_border};
	&:first-of-type {
		margin-top: 0;
	}
`;

const HeaderTitle = styled(FlexColMiddle)`
	row-gap: 0.5rem;
`;

const HeaderStatistic = styled(HeaderTitle)`
	align-items: flex-start;
`;

export function SalesPanel({
	quater,
	editSaleAPI,
	onClickEditSale,
	onClickEditOrder,
	onClickAddFine,
}: {
	quater: GETSalesResponse;
	editSaleAPI: EditSaleAPIFunc;
	onClickEditSale: OnClickEditSaleModal;
	onClickEditOrder: OnClickEditOrderModal;
	onClickAddFine: (eventId: TixEvent['id']) => void;
}) {
	const ordersMissingInfo = useMemo(() => {
		const totalActionNeeded = [];
		const missingInfo: Record<TixEvent['id'], string[]> = {};
		for (const event of quater.events) {
			const missing = Object.values(event.orders)
				.filter((order) => order.paymentTotal === null)
				.map((order) => order.id);
			missingInfo[event.id] = _.uniq(missing);
			totalActionNeeded.push(...missing);
		}
		return { record: missingInfo, list: _.uniq(totalActionNeeded) };
	}, [quater.events]);

	const salesMissingInfo = useMemo(() => {
		const totalActionNeeded = [];
		const missingInfo: Record<TixEvent['id'], string[]> = {};
		for (const event of quater.events) {
			const missing = event.sales
				.filter((sale) => sale.amount === null || sale.tixId.length !== sale.quantity)
				.map((sale) => sale.id);
			missingInfo[event.id] = _.uniq(missing);
			totalActionNeeded.push(...missing);
		}
		return { record: missingInfo, list: _.uniq(totalActionNeeded) };
	}, [quater.events]);

	const needsTransfer = useMemo(() => {
		const totalActionNeeded = [];
		const missingInfo: Record<TixEvent['id'], string[]> = {};
		for (const event of quater.events) {
			const missing = event.sales.filter((sale) => sale.status !== 'completed').map((sale) => sale.id);
			missingInfo[event.id] = _.uniq(missing);
			totalActionNeeded.push(...missing);
		}
		return { record: missingInfo, list: _.uniq(totalActionNeeded) };
	}, [quater.events]);

	const downloadQuaterEventsCSV = async () => {
		try {
			const resp = await requestData('tix.quater-events-csv', { params: { quaterId: quater.quaterId } });
			if (!resp.success) {
				toast.error(resp.error);
				return;
			}

			downloadCSVFile(resp.dataRaw, `events_summary_${quater.quaterId}.csv`);
		} catch (err) {
			console.error(err);
			toast.error(err?.toString() ?? 'Failed to download tax CSV!');
		}
	};

	const downloadQuaterStatsCSV = async () => {
		try {
			const resp = await requestData('tix.quater-stats-csv', { params: { quaterId: quater.quaterId } });
			if (!resp.success) {
				toast.error(resp.error);
				return;
			}

			downloadCSVFile(resp.dataRaw, `vat_summary_${quater.quaterId}.csv`);
		} catch (err) {
			console.error(err);
			toast.error(err?.toString() ?? 'Failed to download tax CSV!');
		}
	};

	const downloadQuaterSecondariesCSV = async () => {
		try {
			const resp = await requestData('tix.quater-secondaries-csv', { params: { quaterId: quater.quaterId } });
			if (!resp.success) {
				toast.error(resp.error);
				return;
			}

			downloadCSVFile(resp.dataRaw, `secondaries_summary_${quater.quaterId}.csv`);
		} catch (err) {
			console.error(err);
			toast.error(err?.toString() ?? 'Failed to download tax CSV!');
		}
	};

	return (
		<>
			<StyledQuaterHeader>
				<HeaderTitle>
					<Text $color="text_title_main" $size="1.8rem" $weight="600">
						{quater.quaterName}
					</Text>
					<Text $color="text_title_alt" $size="1.3rem" $weight="400">
						{quater.events.length} event{quater.events.length === 1 ? '' : 's'}
					</Text>
				</HeaderTitle>

				<StyledEventButtonContainer style={{ rowGap: '0.5rem' }}>
					<ButtonWhite onClick={() => downloadQuaterStatsCSV()} $small={true}>
						DL VAT CSV
					</ButtonWhite>
					<ButtonWhite onClick={() => downloadQuaterEventsCSV()} $small={true}>
						DL Events CSV
					</ButtonWhite>
					<ButtonWhite onClick={() => downloadQuaterSecondariesCSV()} $small={true}>
						DL POS CSV
					</ButtonWhite>
				</StyledEventButtonContainer>
				<StatsHeader
					stats={quater.stats}
					ordersMissingInfo={ordersMissingInfo.list}
					salesMissingInfo={salesMissingInfo.list}
					needsTransfer={needsTransfer.list}
				/>
			</StyledQuaterHeader>

			{quater.events.map((event) => (
				<EventBlock
					event={event}
					editSaleAPI={editSaleAPI}
					onClickEditSale={onClickEditSale}
					onClickEditOrder={onClickEditOrder}
					onClickAddFine={onClickAddFine}
					ordersMissingInfo={ordersMissingInfo.record[event.id]}
					salesMissingInfo={salesMissingInfo.record[event.id]}
					needsTransfer={needsTransfer.record[event.id]}
				/>
			))}
		</>
	);
}

const StyledEventButtonContainer = styled(FlexCol)`
	row-gap: 0.5rem;
	width: 105px;
	button {
		width: 100%;
	}
`;

function EventBlock({
	event,
	editSaleAPI,
	onClickEditSale,
	onClickEditOrder,
	onClickAddFine,
	ordersMissingInfo,
	salesMissingInfo,
	needsTransfer,
}: {
	event: GETSalesResponseEventBASE;
	editSaleAPI: EditSaleAPIFunc;
	onClickEditSale: OnClickEditSaleModal;
	onClickEditOrder: OnClickEditOrderModal;
	onClickAddFine: (eventId: TixEvent['id']) => void;
	ordersMissingInfo: TixOrder['id'][];
	salesMissingInfo: TixSale['id'][];
	needsTransfer: TixSale['id'][];
}) {
	const [showSales, setShowSales] = useState<boolean>(false);

	return (
		<>
			<StyledPanelHeader>
				<HeaderTitle>
					<Text $color="text_title_main" $size="1.8rem" $weight="600">
						{event.event}
					</Text>
					<Text $color="text_title_alt" $size="1.3rem" $weight="400">
						{event.date} - {event.city}
					</Text>
				</HeaderTitle>

				<StyledEventButtonContainer style={{ rowGap: '0.5rem' }}>
					<ButtonRed $small={true} onClick={() => onClickAddFine(event.id)}>
						Add Fine
					</ButtonRed>
					<ButtonGrey onClick={() => setShowSales(!showSales)}>
						{showSales ? 'Hide' : 'Show'} Sales
					</ButtonGrey>
				</StyledEventButtonContainer>
				<StatsHeader
					stats={event.stats}
					ordersMissingInfo={ordersMissingInfo}
					salesMissingInfo={salesMissingInfo}
					needsTransfer={needsTransfer}
				/>
			</StyledPanelHeader>
			{showSales && (
				<Table>
					<TableHead columns={orderColumns}>
						<TH>Prim.</TH>
						<TH>Account</TH>
						<TH>Section</TH>
						<TH>Row</TH>
						<TH>Seat</TH>
						<TH>Cost</TH>
						<TH>T-Type</TH>
						<TH>Secondary</TH>
						<TH>Sale EUR</TH>
						<TH>Sale Native</TH>
						<TH>Sale ID</TH>
						<TH>Order ID</TH>
						<TH>Sale Status</TH>
						<TH>Buyer</TH>
						<TH>Payment date</TH>
						<TH style={{ justifyContent: 'center' }}>Order Actions</TH>
						<TH style={{ justifyContent: 'center' }}>Sale Actions</TH>
					</TableHead>
					<TableBody>
						{event.sales.map((sale) => {
							return (
								<SaleRow
									event={event}
									sale={sale}
									key={sale.id}
									editSaleAPI={editSaleAPI}
									onClickEditSale={onClickEditSale}
									onClickEditOrder={onClickEditOrder}
									ordersMissingInfo={ordersMissingInfo}
									salesMissingInfo={salesMissingInfo}
									needsTransfer={needsTransfer}
								/>
							);
						})}
					</TableBody>
				</Table>
			)}
		</>
	);
}

const lineSpacing = '1rem';
const SaleSeatRow = styled.div`
	border-bottom: 1px solid ${({ theme }) => theme.table_row_sub_divider};
	padding-bottom: ${lineSpacing};
	&:first-of-type {
		padding-top: ${lineSpacing};
	}
	&:last-of-type {
		border-bottom: none;
	}
`;

const StyledSaleSubRowContainer = styled(FlexCol)`
	row-gap: ${lineSpacing};
`;

function SaleRow({
	event,
	sale,
	editSaleAPI,
	onClickEditSale,
	onClickEditOrder,
	ordersMissingInfo,
	salesMissingInfo,
	needsTransfer,
}: {
	event: GETSalesResponseEventBASE;
	sale: TixSale;
	editSaleAPI: EditSaleAPIFunc;
	onClickEditSale: OnClickEditSaleModal;
	onClickEditOrder: OnClickEditOrderModal;
	ordersMissingInfo: TixOrder['id'][];
	salesMissingInfo: TixSale['id'][];
	needsTransfer: TixSale['id'][];
}) {
	const { saleIdsLoading } = useLoadingStore();

	const { valueOfSeats, seats } = useMemo(() => {
		const mappedSeats = sale.tixId.map((v) => event.seats[v]);
		return {
			valueOfSeats: mappedSeats.some((seat) => seat.price === null)
				? null
				: mappedSeats.reduce((total, seat) => total + seat.price!, 0),
			seats: mappedSeats.map((seat) => {
				const order = event.orders[seat.orderId];

				return {
					orderReference: order.reference,
					orderId: seat.orderId,
					primary: event.orders[seat.orderId].primary,
					email: event.orders[seat.orderId].email,
					price: seat.price,
					section: seat.section,
					row: seat.row,
					seat: seat.seat,
					noteAisle: seat.noteAisle,
					noteRV: seat.noteRV,
					noteSide: seat.noteSide,
				};
			}),
		};
	}, [sale, event]);

	const savePaymentTotal = async (amount: string) => {
		const newTotal = !amount ? null : new BigNumber(amount).toNumber();
		await editSaleAPI({
			originalEvent: event,
			saleId: sale.id,
			updatedSale: { amount: newTotal },
		});
	};

	const savePaymentNative = async (amount: string, currency: string) => {
		await editSaleAPI({
			originalEvent: event,
			saleId: sale.id,
			updatedSale: {
				amountNative: !amount || !currency ? null : new BigNumber(amount).toNumber(),
				amountCurrency: !amount || !currency ? null : (currency as TixCurrencyTypes),
			} as Partial<TixSale>,
		});
	};

	const saveSaleStatus = async (status: string) => {
		await editSaleAPI({
			originalEvent: event,
			saleId: sale.id,
			updatedSale: { status: status as TixCompletedStatusTypes },
		});
	};

	const savePaymentDate = async (amountReceivedDate: string) => {
		await editSaleAPI({
			originalEvent: event,
			saleId: sale.id,
			updatedSale: { amountReceivedDate: !amountReceivedDate ? null : amountReceivedDate },
		});
	};

	const saleCompleted = (() => {
		return !salesMissingInfo.includes(sale.id) && !seats.some((seat) => ordersMissingInfo.includes(seat.orderId));
	})();

	const missingSeatsCount = sale.quantity - sale.tixId.length;

	const ButtonSaleStatus = sale.status === TixCompletedStatusTypes.ACTION_NEEDED ? ButtonYellow : ButtonGrey;

	return (
		<TableRow
			$columns={orderColumns}
			key={sale.id}
			$highlighted={true}
			$customBG={
				sale.secondary === TixSecondaryTypes.UNSOLD
					? 'table_row_blue_bg'
					: needsTransfer.includes(sale.id)
						? 'table_row_red_bg'
						: saleCompleted
							? 'table_row_green_bg'
							: 'table_row_yellow_bg'
			}
		>
			<TD>
				<StyledSaleSubRowContainer>
					{seats.map((seat) => (
						<SaleSeatRow>
							<Text $color="text_desc_alt" $size="1.3rem" $weight="400">
								{seat.primary}
							</Text>
						</SaleSeatRow>
					))}
				</StyledSaleSubRowContainer>
			</TD>
			<TD>
				<StyledSaleSubRowContainer>
					{seats.map((seat, index) => (
						<SaleSeatRow key={`account_${sale.id}_${index}`}>
							<Text $color="text_desc_alt" $size="1.3rem" $weight="400">
								{seat.email}
							</Text>
						</SaleSeatRow>
					))}
					{Array.from({ length: missingSeatsCount }).map((_mc, index) => (
						<SaleSeatRow key={`account_missing_${sale.id}_${index}`}>
							<Text $color="text_desc_alt" $size="1.3rem" $weight="400">
								Seat Missing
							</Text>
						</SaleSeatRow>
					))}
				</StyledSaleSubRowContainer>
			</TD>
			<TD>
				<StyledSaleSubRowContainer>
					{seats.map((seat) => (
						<SaleSeatRow>
							<Text $color="text_desc_alt" $size="1.3rem" $weight="400">
								{seat.section}
							</Text>
						</SaleSeatRow>
					))}
				</StyledSaleSubRowContainer>
			</TD>
			<TD>
				<StyledSaleSubRowContainer>
					{seats.map((seat) => (
						<SaleSeatRow>
							<Text $color="text_desc_alt" $size="1.3rem" $weight="400">
								{seat.row}
							</Text>
						</SaleSeatRow>
					))}
				</StyledSaleSubRowContainer>
			</TD>
			<TD>
				<StyledSaleSubRowContainer>
					{seats.map((seat) => {
						const seatNotes = [];
						if (seat.noteSide) seatNotes.push('Side');
						if (seat.noteRV) seatNotes.push('RV');
						if (seat.noteAisle) seatNotes.push('Aisle');
						return (
							<SaleSeatRow>
								<Text $color="text_desc_alt" $size="1.3rem" $weight="400">
									{seat.seat} {seatNotes.length > 0 ? `(${seatNotes.join(', ')})` : ''}
								</Text>
							</SaleSeatRow>
						);
					})}
				</StyledSaleSubRowContainer>
			</TD>
			<TD>
				{sale.transferType !== TixTransferTypes.FINE && (
					<>
						<Icon $colorFill="text_desc_alt" style={{ height: 14 }}>
							<IconSeat />
						</Icon>
						<Text $color="text_desc_alt" $size="1.3rem" $weight="400">
							{valueOfSeats === null ? 'N/A' : formatCurrency(valueOfSeats, 'EUR')}
						</Text>
					</>
				)}
			</TD>
			<TD>
				<Text $color="text_desc_alt" $size="1.3rem" $weight="400">
					{sale.transferType}
				</Text>
			</TD>
			<TD>
				<StyledSaleSubRowContainer>
					<BubbleGrey>{sale.secondary}</BubbleGrey>
				</StyledSaleSubRowContainer>
			</TD>
			<TD>
				<EditCell
					value={(sale.amount ?? '').toString()}
					saveValue={savePaymentTotal}
					onInputMap={checkCurrencyFormatPartly}
					onBlurMap={checkCurrencyFormatFull}
					disabled={saleIdsLoading[sale.id]}
				>
					{sale.amount === null ? 'N/A' : formatCurrency(sale.amount, 'EUR')}
				</EditCell>
			</TD>
			<TD>
				<EditCellCurrency
					value={(sale.amountNative ?? '').toString()}
					saveValues={savePaymentNative}
					valueSelect={sale.amountCurrency ?? ''}
					options={[
						{ value: '', text: 'N/A' },
						{ value: 'GBP', text: 'GBP' },
						{ value: 'USD', text: 'USD' },
						{ value: 'PLN', text: 'PLN' },
						{ value: 'CAD', text: 'CAD' },
						{ value: 'MXN', text: 'MXN' },
						{ value: 'RON', text: 'RON' },
						{ value: 'HUF', text: 'HUF' },
						{ value: 'CHF', text: 'CHF' },
					]}
					onInputMap={checkCurrencyFormatPartly}
					onBlurMap={checkCurrencyFormatFull}
					disabled={saleIdsLoading[sale.id]}
				>
					{sale.amountNative && sale.amountCurrency
						? formatCurrency(sale.amountNative, sale.amountCurrency)
						: 'N/A'}
				</EditCellCurrency>
			</TD>
			<TD>
				<Text $color="text_desc_alt" $size="1.3rem" $weight="400">
					{sale.reference ?? 'N/A'}
				</Text>
				<BubbleGrey style={{ alignSelf: 'center' }}>
					<FlexRowMiddle>
						<Icon $colorFill="bubble_grey_fg" style={{ marginRight: '0.5rem', height: 12 }}>
							<IconSeat />
						</Icon>
						{sale.quantity}
					</FlexRowMiddle>
				</BubbleGrey>
			</TD>
			<TD>
				<StyledSaleSubRowContainer>
					{seats.map((seat) => {
						return (
							<SaleSeatRow>
								<Text $color="text_desc_alt" $size="1.3rem" $weight="400">
									{seat.orderReference}
								</Text>
							</SaleSeatRow>
						);
					})}
				</StyledSaleSubRowContainer>
			</TD>
			<TD>
				<ButtonSaleStatus
					onClick={() =>
						saveSaleStatus(
							sale.status === TixCompletedStatusTypes.ACTION_NEEDED
								? TixCompletedStatusTypes.COMPLETED
								: TixCompletedStatusTypes.ACTION_NEEDED,
						)
					}
					disabled={saleIdsLoading[sale.id]}
				>
					{sale.status}
				</ButtonSaleStatus>
			</TD>
			<TD>
				<StyledSaleSubRowContainer>
					<SaleSeatRow>
						<Text $color="text_desc_alt" $size="1.3rem" $weight="400">
							{sale.buyerEmail ?? 'N/A'}
						</Text>
					</SaleSeatRow>
					<SaleSeatRow>
						<Text $color="text_desc_alt" $size="1.3rem" $weight="400">
							{sale.buyerName ?? 'N/A'}
						</Text>
					</SaleSeatRow>
				</StyledSaleSubRowContainer>
			</TD>
			<TD>
				<EditCell
					value={sale.amountReceivedDate ?? ''}
					saveValue={savePaymentDate}
					onInputMap={checkDateSchemaFormatPartly}
					onBlurMap={checkDateSchemaFormatFull}
					disabled={saleIdsLoading[sale.id]}
				>
					{sale.amountReceivedDate === null ? 'N/A' : sale.amountReceivedDate}
				</EditCell>
			</TD>
			<TD style={{ justifyContent: 'center' }}>
				<StyledSaleSubRowContainer>
					{seats.map((seat) => {
						const actionNeeded = ordersMissingInfo.includes(seat.orderId);
						const ActionButton = actionNeeded ? ButtonYellow : ButtonGrey;
						return (
							<SaleSeatRow>
								<ActionButton
									$small={true}
									onClick={() => onClickEditOrder({ event: event, orderId: seat.orderId })}
								>
									{actionNeeded && <IconWarning />}
									Edit Order
								</ActionButton>
							</SaleSeatRow>
						);
					})}
				</StyledSaleSubRowContainer>
			</TD>
			<TD style={{ justifyContent: 'center' }}>
				{(() => {
					const actionNeeded = salesMissingInfo.includes(sale.id);
					const ActionButton = actionNeeded ? ButtonYellow : ButtonGrey;
					return (
						<ActionButton onClick={() => onClickEditSale({ event: event, saleId: sale.id })}>
							{actionNeeded && <IconWarning />}
							Edit Sale
						</ActionButton>
					);
				})()}
			</TD>
		</TableRow>
	);
}

function StatsHeader({
	stats,
	salesMissingInfo,
	ordersMissingInfo,
	needsTransfer,
}: {
	stats?: StatsBase;
	salesMissingInfo: TixSale['id'][];
	ordersMissingInfo: TixOrder['id'][];
	needsTransfer: TixSale['id'][];
}) {
	return (
		<>
			<HeaderStatistic>
				<Text $color={needsTransfer.length > 0 ? 'text_red' : 'text_title_alt'} $size="1.3rem" $weight="400">
					{needsTransfer.length} Transfer(s)
				</Text>
				<Text
					$color={salesMissingInfo.length > 0 ? 'text_orange' : 'text_title_alt'}
					$size="1.3rem"
					$weight="400"
				>
					{salesMissingInfo.length} Sales(s)
				</Text>
				<Text
					$color={ordersMissingInfo.length > 0 ? 'text_orange' : 'text_title_alt'}
					$size="1.3rem"
					$weight="400"
				>
					{ordersMissingInfo.length} Orders(s)
				</Text>
			</HeaderStatistic>
			<OSStatsHeader stats={stats} />
		</>
	);
}
